import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isDefined, isNotDefined } from '@sgme/fp';
import { useEffect, useRef, useState } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { EmailsInput } from '@/components/ui/EmailsInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import type { AlgoOrder } from '@/store/orders/algoOrders/state.ts';
import type { CallOrder } from '@/store/orders/call/state.ts';
import { useGetOrderRequestedEmailsQuery } from '@/store/orders/EmailsRequest.ts';
import type { FixingOrder } from '@/store/orders/fixing/state.ts';
import type { ValidateOrderResponse } from '@/store/orders/state.ts';
import type { StopLossOrder } from '@/store/orders/stopLoss/state.ts';
import type { TakeProfitOrder } from '@/store/orders/takeProfitOrder/state.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import type { OmsErrors } from '@/types/common.ts';

type OrderNotificationsProps = {
  tileId: string;
  notificationEmails: string[];
  errors: OmsErrors | undefined;
  validate: (
    updatedProperties:
      | Partial<StopLossOrder['properties']>
      | Partial<TakeProfitOrder['properties']>
      | Partial<CallOrder['properties']>
      | Partial<FixingOrder['properties']>
      | Partial<AlgoOrder['properties']>,
  ) => Promise<
    | {
        data: ValidateOrderResponse;
        error: undefined;
      }
    | {
        data: undefined;
        error: FetchBaseQueryError | SerializedError;
      }
    | undefined
  >;
};

export const OrderNotifications = (props: OrderNotificationsProps) => {
  const { notificationEmails, errors, validate, tileId } = props;

  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));
  const [defaultEmailsByBdrId, setDefaultEmailsByBdrId] = useState<string[]>([]);
  const { data: defaultEmails } = useGetOrderRequestedEmailsQuery(clientId);
  const track = useTrackEvent();

  const handleChange = (notificationEmails: string[]) => {
    track('Order action', 'Notification emails changed');
    validate({ notificationEmails });
  };

  const defaultEmailsRef = useRef<string[]>([]);

  // oxlint-disable-next-line exhaustive-deps -- We should only run this effect when client changes
  useEffect(() => {
    if (
      isNotDefined(defaultEmails) ||
      notificationEmails.some((notifEmail) => defaultEmails.includes(notifEmail)) ||
      // Check if defaultEmails have changed since last run
      (defaultEmailsRef.current.length === defaultEmails.length &&
        defaultEmailsRef.current.every((email, index) => email === defaultEmails[index]))
    ) {
      return;
    }

    const filteredCurrentEmails = notificationEmails?.filter((email) => !defaultEmailsByBdrId.includes(email)) ?? [];

    const newNotificationEmails = [...filteredCurrentEmails, ...defaultEmails];

    validate({ notificationEmails: newNotificationEmails });

    setDefaultEmailsByBdrId(defaultEmails);
    defaultEmailsRef.current = defaultEmails;
  }, [defaultEmails]);

  return (
    <OrderRow alignTop labelId="order.fields.notificationEmails.label">
      <OmsFieldErrorWrapper errors={errors?.notificationEmails}>
        <EmailsInput
          emails={notificationEmails}
          onChange={handleChange}
          invalid={isDefined(errors?.notificationEmails)}
          dataE2e="order-notifications-emails"
          maxHeight={96} // rough estimate for 4 lines of badges
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
